import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SimplePageLayout } from "../../components/simplePage/simplePageLayout.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import {
  getEmailTemplate,
  getEmailTemplateInfo,
  saveEmailTemplate,
} from "../../api/emailTemplate.ts";
import type { PartialEmailTemplate, ValidEmailAddress } from "../../api/emailTemplate.ts";
import styles from "../../styles/formPage.module.scss";

function b64ToUtf8(b64: string): string {
  if (!b64) return "";
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

function extractEmailAndName(fullAddress: string): { email: string; name: string } {
  const emailMatch = fullAddress.match(/<([^>]+)>/);
  const email = emailMatch?.[1] ?? fullAddress;
  const rawName = fullAddress.replace(`<${email}>`, "").trim().replace(/"/g, "");
  return { email, name: rawName === email ? "" : rawName };
}

interface EditEmailTemplatePageProps {
  /** When opened as a popup (dialog-over-grid) instead of a route page. */
  popupTemplateId?: string;
  onClose?: () => void;
  onSaved?: () => void;
}

export function EditEmailTemplatePage({ popupTemplateId, onClose, onSaved }: EditEmailTemplatePageProps = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPopup = onClose != null;
  const templateId = popupTemplateId != null
    ? (parseInt(popupTemplateId, 10) || 0)
    : parseInt(searchParams.get("id") ?? "0", 10);

  const [template, setTemplate] = useState<PartialEmailTemplate | null>(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [senderName, setSenderName] = useState("");
  const [fromAddressEmail, setFromAddressEmail] = useState("");
  const [replyName, setReplyName] = useState("");
  const [replyAddressEmail, setReplyAddressEmail] = useState("");
  const [validEmails, setValidEmails] = useState<ValidEmailAddress[]>([]);
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  // Tracks unsaved edits so the popup can warn before discarding on close.
  const [dirty, setDirty] = useState(false);

  // Dedupe by templateId — StrictMode's dev double-mount otherwise fires
  // getEmailTemplate + getEmailTemplateInfo twice (4 round-trips). Reused
  // in-flight Promise tuple collapses them to one of each. Entry clears
  // on settle so editing the URL to a different template still reloads.
  const inFlightRef = useRef<{ id: number; promise: Promise<[
    Awaited<ReturnType<typeof getEmailTemplate>>,
    Awaited<ReturnType<typeof getEmailTemplateInfo>>,
  ]> } | null>(null);

  const loadData = useCallback(async (cancelledRef?: { current: boolean }) => {
    const cancelled = cancelledRef ?? { current: false };
    if (!templateId || templateId <= 0) return;
    setLoading(true);
    setStatus(null);
    try {
      let promise: NonNullable<typeof inFlightRef.current>["promise"];
      if (inFlightRef.current?.id === templateId) {
        promise = inFlightRef.current.promise;
      } else {
        promise = Promise.all([getEmailTemplate(templateId), getEmailTemplateInfo()]);
        inFlightRef.current = { id: templateId, promise };
      }
      const [tmplData, infoData] = await promise;
      if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      if (cancelled.current) return;
      setValidEmails(infoData.ValidEmailAddresses);
      const decoded = b64ToUtf8(tmplData.Template);
      setTemplate(tmplData);
      setHtmlContent(decoded);
      setTemplateName(tmplData.TemplateName ?? "");
      setSubject(tmplData.Subject ?? "");

      const from = extractEmailAndName(tmplData.FromAddress ?? "");
      setFromAddressEmail(from.email);
      setSenderName(from.name);

      const reply = extractEmailAndName(tmplData.ReplyToAddress ?? "");
      setReplyAddressEmail(reply.email);
      setReplyName(reply.name);
      setDirty(false);
    } catch (err: unknown) {
      if (!cancelled.current) setStatus({ type: "error", message: String(err) });
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    const cancelledRef = { current: false };
    void loadData(cancelledRef);
    return () => { cancelledRef.current = true; };
  }, [loadData]);

  const handleSave = async () => {
    if (!template) return;
    if (!fromAddressEmail) { setStatus({ type: "error", message: "Select a From Address." }); return; }
    if (!replyAddressEmail) { setStatus({ type: "error", message: "Select a Reply To Address." }); return; }
    setSaving(true);
    setStatus(null);
    try {
      const fromAddr = senderName.trim()
        ? `"${senderName.trim()}" <${fromAddressEmail}>`
        : fromAddressEmail;
      const replyAddr = replyName.trim()
        ? `"${replyName.trim()}" <${replyAddressEmail}>`
        : replyAddressEmail;
      await saveEmailTemplate({
        ...template,
        TemplateName: templateName,
        Subject: subject,
        FromAddress: fromAddr,
        ReplyToAddress: replyAddr,
        Template: htmlContent,
      });
      setStatus({ type: "success", message: "Template saved." });
      setDirty(false);
      if (isPopup) {
        onSaved?.();
        onClose?.();
      } else {
        navigate(-1);
      }
    } catch (err: unknown) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${templateName || "template"}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const secondaryActions = (
    <>
      <button className={styles.secondaryBtn} onClick={() => setIsPreview((p) => !p)}>
        {isPreview ? "Show Code" : "Preview"}
      </button>
      <button className={styles.secondaryBtn} onClick={() => void loadData()}>Reload</button>
      <button className={styles.secondaryBtn} onClick={handleDownload}>Download</button>
    </>
  );

  const formBody = (
    <>
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <div>
          <div className={styles.fieldRow}>
            <label>Template Name</label>
            <input type="text" value={templateName} onChange={(e) => { setTemplateName(e.target.value); setDirty(true); }} className={styles.fieldInput} />
          </div>
          <div className={styles.fieldRow}>
            <label>From Address</label>
            <select value={fromAddressEmail} onChange={(e) => { setFromAddressEmail(e.target.value); setDirty(true); }} className={styles.fieldInput}>
              <option value="">— Select —</option>
              {validEmails.map((a) => <option key={a.AddressID} value={a.Email}>{a.Email}</option>)}
            </select>
          </div>
          <div className={styles.fieldRow}>
            <label>Sender Name</label>
            <input type="text" value={senderName} onChange={(e) => { setSenderName(e.target.value); setDirty(true); }} className={styles.fieldInput} />
          </div>
        </div>
        <div>
          <div className={styles.fieldRow}>
            <label>Subject</label>
            <input type="text" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} className={styles.fieldInput} />
          </div>
          <div className={styles.fieldRow}>
            <label>Reply To Address</label>
            <select value={replyAddressEmail} onChange={(e) => { setReplyAddressEmail(e.target.value); setDirty(true); }} className={styles.fieldInput}>
              <option value="">— Select —</option>
              {validEmails.map((a) => <option key={a.AddressID} value={a.Email}>{a.Email}</option>)}
            </select>
          </div>
          <div className={styles.fieldRow}>
            <label>Reply Name</label>
            <input type="text" value={replyName} onChange={(e) => { setReplyName(e.target.value); setDirty(true); }} className={styles.fieldInput} />
          </div>
        </div>
      </div>

      <div style={{ marginTop: "var(--space-3)" }}>
        <label style={{ fontWeight: 600, display: "block", marginBottom: "var(--space-1)" }}>Template</label>
        {isPreview ? (
          <div
            style={{
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-2)",
              padding: "var(--space-2)",
              minHeight: "60vh",
              background: "var(--color-bg)",
              overflow: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <textarea
            value={htmlContent}
            onChange={(e) => { setHtmlContent(e.target.value); setDirty(true); }}
            style={{
              width: "100%",
              minHeight: "60vh",
              fontFamily: "var(--font-family-mono, monospace)",
              fontSize: "var(--text-sm)",
              lineHeight: 1.5,
              tabSize: 2,
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-2)",
              padding: "var(--space-3)",
              background: "var(--color-bg-muted)",
              color: "var(--color-text)",
              resize: "vertical",
            }}
            spellCheck={false}
          />
        )}
      </div>
    </>
  );

  if (!templateId || templateId <= 0) {
    const msg = <p>No template ID specified.</p>;
    return isPopup
      ? <GridEditModal title="Edit Email Template" readOnly cancelLabel="Close" onClose={onClose!} onSave={() => { /* read-only */ }}>{msg}</GridEditModal>
      : <SimplePageLayout title="Edit Email Template">{msg}</SimplePageLayout>;
  }

  // Popup (dialog-over-grid) mode — wrap in the shared modal chrome. GridEditModal
  // owns Save/Cancel; the Preview/Reload/Download actions sit left in the footer.
  if (isPopup) {
    return (
      <GridEditModal
        title="Edit Email Template"
        wide
        maxWidth="1200px"
        saving={saving}
        loading={loading}
        statusMessage={status ? { type: status.type, message: status.message } : null}
        unsaved={dirty}
        footerLeft={secondaryActions}
        onClose={onClose!}
        onSave={handleSave}
      >
        {formBody}
      </GridEditModal>
    );
  }

  // Route-page mode.
  if (loading) {
    return <SimplePageLayout title="Edit Email Template"><p>Loading...</p></SimplePageLayout>;
  }

  return (
    <SimplePageLayout title="Edit Email Template">
      {status && <div className={`${styles.statusMsg} ${status.type === "error" ? styles.statusError : styles.statusSuccess}`}>{status.message}</div>}

      <div style={{ display: "flex", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        {secondaryActions}
        <button className={styles.primaryBtn} onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {formBody}
    </SimplePageLayout>
  );
}
